(function () {
  "use strict";

  const CONTRACT_VERSION = 1;
  const AUTO_VALUE = "__auto__";
  const FOLLOW_CONTENT_VALUE = "__content__";
  const REGION_DEFAULT_VALUE = "__region-default__";
  const SERVER_DATASPACE_ALIASES = Object.freeze({
    sea: "asia",
    th: "asia",
  });
  const BRAND = Symbol.for("fishystuff.language-config-contract.v1");

  function trimString(value) {
    return String(value ?? "").trim();
  }

  function isRecord(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === null || Object.getPrototypeOf(prototype) === null;
  }

  function dataLanguageCode(value) {
    const code = trimString(value);
    return /^[a-z0-9_]{1,32}$/.test(code) ? code : "";
  }

  function serverDataspaceCode(value) {
    const code = trimString(value).toLowerCase();
    const canonical = SERVER_DATASPACE_ALIASES[code] || code;
    return /^[a-z0-9_-]+$/.test(canonical) ? canonical : "";
  }

  function normalizeDataLanguages(values) {
    if (!Array.isArray(values)) return Object.freeze([]);
    const seen = new Set();
    const languages = [];
    for (const value of values) {
      const code = dataLanguageCode(value);
      if (!code || seen.has(code)) continue;
      seen.add(code);
      languages.push(code);
    }
    return Object.freeze(languages);
  }

  function requireText(value, field) {
    const text = trimString(value);
    if (!text) throw new TypeError(`${field} must be non-empty text.`);
    return text;
  }

  function normalizeContentLanguages(values) {
    if (!Array.isArray(values) || values.length === 0) {
      throw new TypeError("Language config requires content languages.");
    }
    const seen = new Set();
    return Object.freeze(values.map((value, index) => {
      if (!isRecord(value)) {
        throw new TypeError(`Content language ${index} must be an object.`);
      }
      const code = requireText(value.code, `Content language ${index} code`);
      if (seen.has(code)) throw new TypeError(`Duplicate content language ${code}.`);
      seen.add(code);
      const dataLanguage = dataLanguageCode(value.dataLanguage);
      if (!dataLanguage) {
        throw new TypeError(`Content language ${code} has an invalid data language.`);
      }
      return Object.freeze({
        code,
        pathPrefix: requireText(value.pathPrefix, `Content language ${code} path prefix`),
        dataLanguage,
      });
    }));
  }

  function normalizeLocaleLanguages(values) {
    if (!Array.isArray(values) || values.length === 0) {
      throw new TypeError("Language config requires locale languages.");
    }
    const seen = new Set();
    return Object.freeze(values.map((value, index) => {
      const code = requireText(value, `Locale language ${index}`);
      if (seen.has(code)) throw new TypeError(`Duplicate locale language ${code}.`);
      seen.add(code);
      return code;
    }));
  }

  function normalizeServerDataspaces(values) {
    if (!Array.isArray(values) || values.length === 0) {
      throw new TypeError("Language config requires server dataspaces.");
    }
    const seen = new Set();
    return Object.freeze(values.map((value, index) => {
      if (!isRecord(value)) {
        throw new TypeError(`Server dataspace ${index} must be an object.`);
      }
      const code = serverDataspaceCode(value.code);
      if (!code) throw new TypeError(`Server dataspace ${index} has an invalid code.`);
      if (seen.has(code)) throw new TypeError(`Duplicate server dataspace ${code}.`);
      seen.add(code);
      const dataLanguages = normalizeDataLanguages(value.dataLanguages);
      if (dataLanguages.length === 0) {
        throw new TypeError(`Server dataspace ${code} requires data languages.`);
      }
      const defaultApiLang = dataLanguageCode(value.defaultApiLang);
      if (!defaultApiLang || !dataLanguages.includes(defaultApiLang)) {
        throw new TypeError(
          `Server dataspace ${code} default data language must belong to its catalog.`,
        );
      }
      return Object.freeze({
        code,
        sourceServiceRegion: requireText(
          value.sourceServiceRegion,
          `Server dataspace ${code} source service region`,
        ),
        dataLanguages,
        defaultApiLang,
      });
    }));
  }

  const generated = window.__fishystuffGeneratedI18n;
  const config = generated?.config;
  if (!isRecord(config)) {
    throw new TypeError("Generated language configuration is unavailable.");
  }

  const contentLanguages = normalizeContentLanguages(config.contentLanguages);
  const localeLanguages = normalizeLocaleLanguages(config.localeLanguages);
  const serverDataspaces = normalizeServerDataspaces(config.serverDataspaces);
  const apiLanguages = normalizeDataLanguages(config.apiLanguages);
  if (apiLanguages.length === 0) {
    throw new TypeError("Language config requires initial API data languages.");
  }

  const defaultContentLang = requireText(
    config.defaultContentLang,
    "Default content language",
  );
  if (!contentLanguages.some((language) => language.code === defaultContentLang)) {
    throw new TypeError("Default content language is not configured.");
  }
  const defaultLocale = requireText(config.defaultLocale, "Default locale");
  if (!localeLanguages.includes(defaultLocale)) {
    throw new TypeError("Default locale is not configured.");
  }
  const defaultServerDataspace = serverDataspaceCode(config.defaultServerDataspace);
  if (!serverDataspaces.some((dataspace) => dataspace.code === defaultServerDataspace)) {
    throw new TypeError("Default server dataspace is not configured.");
  }
  const defaultApiLang = dataLanguageCode(config.defaultApiLang);
  if (!defaultApiLang) {
    throw new TypeError("Default API data language is invalid.");
  }

  function isLocale(value) {
    return typeof value === "string" && localeLanguages.includes(value);
  }

  function isDataLanguage(value) {
    return typeof value === "string" && dataLanguageCode(value) === value;
  }

  function isServerDataspace(value) {
    return typeof value === "string"
      && serverDataspaces.some((dataspace) => dataspace.code === value);
  }

  window.__fishystuffLanguageConfigV1 = Object.freeze({
    [BRAND]: CONTRACT_VERSION,
    apiLanguages,
    autoValue: AUTO_VALUE,
    canonicalServerDataspaceCode: serverDataspaceCode,
    contentLanguages,
    defaultApiLang,
    defaultContentLang,
    defaultLocale,
    defaultServerDataspace,
    followContentValue: FOLLOW_CONTENT_VALUE,
    isDataLanguage,
    isLocale,
    isServerDataspace,
    localeLanguages,
    normalizeDataLanguages,
    regionDefaultValue: REGION_DEFAULT_VALUE,
    serverDataspaces,
  });
})();
